import { STATE_PROVINCE_REQUIRED_COUNTRIES, SecondaryApiTypeEnum } from '@tassat/common-definitions';
import {
    Asset,
    FirmRecord as FredFirmRecord,
    UserRecord as FredUserRecord,
    UserSecondaryApiTypeRecord
} from '@tassat/fred-definitions';
import { WalletRecordMapped } from '@tassat/fred-extended-definitions';
import {
    FirmRecord as PaymentsFirmRecord,
    UserRecord as PaymentsUserRecord
} from '@tassat/payments-definitions';
import {
    ActionTypes,
    UserAccountMapped
} from '@tassat/ui-definitions';

import { IErrors, IFormProps, IFormValues } from './types';
import {
    textFieldCharacterWhitelist,
    validateAlphaNumeric,
    validateAssetRemoval,
    validateCompanyCode,
    validateCompanyCodeUniqueness,
    validateEmailFormatting,
    validateFirmAssetsRequired,
    validateLengthRequirement,
    validateNoExcelFormulaStartingCharacters,
    validateNoNonPrintable
} from '../helpers/validationHelpers';

export const validateFirmForm = async (values: IFormValues, opts: {
    actionType: ActionTypes;
    initialValues: IFormValues;
    firms: PaymentsFirmRecord[] | FredFirmRecord[];
    firmUsers: PaymentsUserRecord[] | FredUserRecord[];
    firmEoaAccounts?: Array<WalletRecordMapped>;
    userAccounts: UserAccountMapped[];
    features?: IFormProps['features'];
    getCollateral?: () => { data: any };
    availableAssets?: Asset[];
    firmAllApiTypeUsers?: UserSecondaryApiTypeRecord[];
}) => {
    const {
        actionType,
        initialValues,
        firms,
        firmUsers,
        firmEoaAccounts,
        userAccounts,
        features,
        getCollateral,
        availableAssets,
        firmAllApiTypeUsers
    } = opts;

    const errors = {} as IErrors;

    const firmName = values.Name.trim();
    const initialName = initialValues.Name.trim();
    if (!firmName) {
        errors.Name = 'Required';
    } else if (
        actionType === ActionTypes.add ||
        (actionType === ActionTypes.edit && firmName !== initialName)
    ) {
        const firmNameError = firms
            .some((firm) => firm.Name.trim()
                .toLowerCase() === firmName.toLowerCase());
        const closedFirmNameError = firms
            .some((firm) => (
                firm.Name.trim()
                    .toLowerCase() === firmName.toLowerCase() &&
                    firm.IsClosed
            ));

        const firmNameExcelError = validateNoExcelFormulaStartingCharacters(firmName);

        if (closedFirmNameError) errors.Name = 'Could not add Firm: Firm exists in database and is closed';
        else if (firmNameError) errors.Name = 'Firm name must be unique';
        else if (firmNameExcelError) errors.Name = firmNameExcelError;

        const firmNameWhitelistCheckError = textFieldCharacterWhitelist(firmName);

        if (firmNameWhitelistCheckError) errors.Name = firmNameWhitelistCheckError;
    }

    if (
        actionType === ActionTypes.edit &&
        values.IsTokenAPIFirm !== initialValues.IsTokenAPIFirm &&
        !values.IsTokenAPIFirm && firmUsers.some((user) => user.IsTokenAPIUser)
    ) {
        errors.IsTokenAPIFirm = 'Cannot disable API flag with API users in firm';
    }

    if (
        actionType === ActionTypes.edit &&
        values.IsCollateral !== initialValues.IsCollateral &&
        !values.IsCollateral &&
        getCollateral
    ) {
        try {
            const response = await getCollateral();
            if (response.data && response.data.TotalCount > 0) {
                errors.IsCollateral = 'Unable to disable collateral functionality due to active/pending pledges.';
            }
        } catch (error) {
            errors.IsCollateral = 'Cannot disable, please try again';
        }
    }

    const notes = values.Notes?.trim();
    const notesExcelError = validateNoExcelFormulaStartingCharacters(notes);
    if (notesExcelError) errors.Notes = notesExcelError;

    const notesGarbageCharsError = textFieldCharacterWhitelist(notes);
    if (notesGarbageCharsError) {
        errors.Notes = notesGarbageCharsError;
    }

    const emailExcelError = validateNoExcelFormulaStartingCharacters(values.FirmEmail);
    const emailNonPrintableError = validateNoNonPrintable(values.FirmEmail);
    if (!values.FirmEmail) {
        errors.FirmEmail = 'Required';
    } else if (emailExcelError) {
        errors.FirmEmail = emailExcelError;
    } else if (validateEmailFormatting(values.FirmEmail, true)) {
        errors.FirmEmail = 'Please enter a valid email address';
    } else if (emailNonPrintableError) {
        errors.FirmEmail = emailNonPrintableError;
    }

    if (!values.Country?.value) {
        errors.Country = 'Required';
    }

    if (
        values.Country?.value &&
        STATE_PROVINCE_REQUIRED_COUNTRIES.includes(values.Country.value as string) &&
        !values.State?.value
    ) {
        errors.State = 'Required';
    }

    const removedAssets = actionType === ActionTypes.edit ? (initialValues.AssetMapping || [])
        .filter((initialAsset) => !values.AssetMapping?.some((asset) => asset.value === initialAsset.value))
        .map((initialAsset) => ({
            ticker: initialAsset.value as string,
            assetId: availableAssets?.find((asset) => asset.Ticker === initialAsset.value)?.AssetId
        }))
        .filter(({ assetId }) => !!assetId) : [];

    const assetRemovalError = removedAssets.length > 0 ? validateAssetRemoval(removedAssets, firmEoaAccounts) : null;

    if (assetRemovalError) {
        errors.AssetMapping = assetRemovalError;
    } else {
        const assetsRequiredError = validateFirmAssetsRequired(values.AssetMapping);
        if (assetsRequiredError) {
            errors.AssetMapping = assetsRequiredError;
        }
    }

    if (features?.SSO && features.SSOConfig && features.SSOConfig.CompanyCodeRegEx) {
        const validateCompanyCodeError = validateCompanyCode(values.CompanyCode, features.SSOConfig.CompanyCodeRegEx);
        const validateCompanyCodeUniquenessError = validateCompanyCodeUniqueness(values.CompanyCode, firms, initialValues?.CompanyCode);

        if (validateCompanyCodeError) {
            errors.CompanyCode = validateCompanyCodeError;
        } else if (validateCompanyCodeUniquenessError) {
            errors.CompanyCode = validateCompanyCodeUniquenessError;
        }
    }

    const leiLength = validateLengthRequirement(values.Lei, 20, true);
    const leiAlphanumeric = validateAlphaNumeric(values.Lei);
    const leiDuplicate = firms.some((firm) => firm.Lei.toLowerCase() === values.Lei.toLowerCase());
    if (!values.Lei.trim()) {
        errors.Lei = 'Required';
    } else if (leiAlphanumeric) {
        errors.Lei = leiAlphanumeric;
    } else if (leiLength) {
        errors.Lei = leiLength;
    } else if (leiDuplicate &&
        (actionType === ActionTypes.add ||
            (actionType === ActionTypes.edit && initialValues.Lei.toLowerCase() !== values.Lei.toLowerCase()))) {
        errors.Lei = 'Lei must be unique';
    }

    // FRED-3379: LF flag can only be turned off if no LF wallets in Firm
    if (actionType === ActionTypes.edit &&
        values.IsLiquidityFacility !== initialValues.IsLiquidityFacility &&
        !values.IsLiquidityFacility
    ) {
        if (firmEoaAccounts?.some((account) => account.wallet.IsLiquidityFacility)) {
            errors.IsLiquidityFacility = 'Cannot disable LF Flag with LF Accounts in Firm';
        }
    }

    if (features?.fireblocks.api) {
        if (
            actionType === ActionTypes.edit &&
            (initialValues.APISecondaryConnectionType && !values.APISecondaryConnectionType)
        ) {
            // TODO Update to handle multiple secondary API connections
            const doesMapUserExist = firmAllApiTypeUsers?.some(
                (userType) => userType.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS
            );

            if (doesMapUserExist) {
                errors.APISecondaryConnectionType = 'Cannot disable Fireblocks Flag with Fireblocks Users in Firm';
            }
        }
    }

    return errors;
};